<template>
    <div>
        <v-overlay :value="loading" absolute>
            <v-progress-circular indeterminate size="64" color="primary" />
        </v-overlay>
        <!-- ── Add New Position dialog — must be OUTSIDE <v-form> so its inputs
             are not registered with the main form's validate() call ── -->
        <v-dialog v-model="newPosDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <span class="headline">{{ $t('setupposition.newposition') }}</span>
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    outlined dense
                                    :label="$t('columns.position')"
                                    :rules="[rules.required]"
                                    v-model="newPos.poscode"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" class="mt-n4">
                                <v-text-field
                                    outlined dense
                                    :label="$t('columns.description')"
                                    :rules="[rules.required]"
                                    v-model="newPos.posdesc"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" class="mt-n4">
                                <v-checkbox
                                    v-model="newPos.isdisable"
                                    :label="$t('columns.disable')"
                                    dense
                                ></v-checkbox>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" class="mt-n4">
                                <v-text-field
                                    outlined dense
                                    :label="$t('columns.remark')"
                                    v-model="newPos.remark"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-card-text v-if="newPosErr" class="mt-n4">
                    <span style="font-weight:bold; color:red">{{ newPosErrMsg }}</span>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red darken-1" text @click="closeNewPosDialog">{{ $t('button.cancel') }}</v-btn>
                    <v-btn color="green darken-1" text @click="saveNewPosition">{{ $t('button.save') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-container>
            <v-form ref="form">
                <v-row>
                    <h2 class="pb-5">
                        {{ this.parentObject.pageTitle }}
                        <span class="page_title_value">{{ this.parentObject.pageTitleValue }}</span>
                    </h2>
                </v-row>

                <v-container
                    id="scroll-target"
                    class="custom-form overflow-y-auto px-5"
                >
                    <!-- ── Parent fields ── -->
                    <!-- New mode: pick from position dropdown -->
                    <v-row v-if="!parentObject.isEdit">
                        <v-col cols="9" sm="9" class="d-flex align-center pb-n5">
                            <v-autocomplete
                                outlined dense
                                :label="$t('columns.position')"
                                :items="positionList"
                                :rules="[rules.required]"
                                v-model="formData.positionid"
                                @change="changePositionHandler(formData.positionid)"
                                class="flex-grow-1"
                            ></v-autocomplete>
                            <v-btn
                                icon small color="primary"
                                @click="newPosDialog = true"
                                title="Add new position"
                                class="ml-2 mb-5"
                            >
                                <v-icon>mdi-plus-circle</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <!-- Edit mode: edit position code directly -->
                    <v-row v-else>
                        <v-col cols="9" sm="9" class="pb-n5">
                            <v-text-field
                                outlined dense
                                :label="$t('columns.position')"
                                :rules="[rules.required]"
                                v-model="formData.poscode"
                                @input="updateValue"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="10" sm="10" class="pb-n5 mt-n8">
                            <v-text-field
                                outlined dense
                                :label="$t('columns.description')"
                                :rules="[rules.required]"
                                v-model="formData.posdesc"
                                @input="updateValue"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="6" sm="6" class="pb-n4 mt-n8">
                            <v-checkbox
                                v-model="formData.isdisable"
                                :label="$t('columns.disable')"
                                @change="updateValue"
                            ></v-checkbox>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="10" sm="10" class="mt-n4">
                            <v-text-field
                                outlined dense
                                :label="$t('columns.remark')"
                                v-model="formData.remark"
                                @input="updateValue"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- ── Child detail fields ── -->
                    <v-row>
                        <v-col cols="12" class="mt-4 mb-2">
                            <v-divider></v-divider>
                            <h3 class="section-heading mt-4">{{ $t('setupposition.positiondetails') }}</h3>
                        </v-col>
                    </v-row>

                    <!-- Date range and positioncount -->
                    <v-row>
                        <v-col cols="4" sm="4" class="pb-n5">
                            <v-text-field
                                outlined
                                :label="$t('setupposition.effectivestartdate')"
                                type="date"
                                ref="startdate"
                                :rules="[rules.required]"
                                v-model="formData.startdate"
                                @input="updateValue"
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n5">
                            <v-text-field
                                outlined
                                :label="$t('setupposition.effectiveenddate')"
                                type="date"
                                ref="enddate"
                                :rules="[rules.required]"
                                v-model="formData.enddate"
                                @input="updateValue"
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="2" sm="2" class="pb-n5">
                            <v-text-field
                                outlined
                                :label="$t('setupposition.count')"
                                type="number"
                                v-model.number="formData.positioncount"
                                @input="updateValue"
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- Reporting position -->
                    <v-row>
                        <v-col cols="8" sm="8" class="pb-n5 mt-n2">
                            <v-autocomplete
                                outlined
                                :label="$t('setupposition.reportingposition')"
                                :items="reportingPositionList"
                                v-model="formData.repposid"
                                @input="updateValue"
                                @change="changeReportingPositionHandler(formData.repposid)"
                                dense
                                clearable
                            ></v-autocomplete>
                        </v-col>
                        <v-col cols="2" sm="2" class="pb-n5 mt-n2">
                            <v-text-field
                                outlined
                                readonly
                                :label="$t('columns.code')"
                                v-model="reportingPositionCode"
                                background-color="#ffeab2"
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- Org scope inside a Settings tab -->
                    <v-row>
                        <v-tabs fixed-tabs>
                            <v-tab>{{ $t('settings') }}</v-tab>

                            <v-tab-item class="pt-4">
                                <v-row>
                                    <v-col cols="5" sm="5">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.company')"
                                            :items="companyList"
                                            v-model="formData.compnyid"
                                            @input="updateValue"
                                            @change="changeCompanyHandler(formData.compnyid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="companyCode"></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="5" sm="5" class="pb-n4 mt-n8">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.branch')"
                                            :items="branchList"
                                            v-model="formData.brchid"
                                            @input="updateValue"
                                            @change="changeBranchHandler(formData.brchid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="branchCode"></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="5" sm="5" class="pb-n4 mt-n8">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.division')"
                                            :items="divisionList"
                                            v-model="formData.divnid"
                                            @input="updateValue"
                                            @change="changeDivisionHandler(formData.divnid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="divisionCode"></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="5" sm="5" class="pb-n4 mt-n8">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.department')"
                                            :items="departmentList"
                                            v-model="formData.deptid"
                                            @input="updateValue"
                                            @change="changeDepartmentHandler(formData.deptid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="departmentCode"></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="5" sm="5" class="pb-n4 mt-n8">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.section')"
                                            :items="sectionList"
                                            v-model="formData.sectioid"
                                            @input="updateValue"
                                            @change="changeSectionHandler(formData.sectioid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="sectionCode"></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="5" sm="5" class="pb-n4 mt-n8">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.unit')"
                                            :items="unitList"
                                            v-model="formData.unitid"
                                            @input="updateValue"
                                            @change="changeUnitHandler(formData.unitid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="unitCode"></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="5" sm="5" class="pb-n4 mt-n8">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.job')"
                                            :items="jobList"
                                            v-model="formData.jobid"
                                            @input="updateValue"
                                            @change="changeJobHandler(formData.jobid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="jobCode"></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="5" sm="5" class="pb-n4 mt-n8">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.grade')"
                                            :items="gradeList"
                                            v-model="formData.grdid"
                                            @input="updateValue"
                                            @change="changeGradeHandler(formData.grdid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="gradeCode"></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="5" sm="5" class="pb-n4 mt-n8">
                                        <v-autocomplete
                                            outlined dense clearable
                                            :label="$t('columns.employmenttype')"
                                            :items="emptypeList"
                                            v-model="formData.emptypeid"
                                            @input="updateValue"
                                            @change="changeEmpTypeHandler(formData.emptypeid)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                                        <v-text-field outlined dense readonly background-color="#ffeab2"
                                            :label="$t('columns.code')" v-model="emptypeCode"></v-text-field>
                                    </v-col>
                                </v-row>
                            </v-tab-item>
                        </v-tabs>
                    </v-row>
                </v-container>

                <app-formbuttons
                    v-bind:EditBool="this.parentObject.isEdit"
                    v-on:NewPressed="newHandler"
                    v-on:SavePressed="saveHandler"
                    v-on:DeletePressed="deleteHandler"
                    v-on:CancelPressed="cancelHandler"
                />
            </v-form>
        </v-container>
    </div>
</template>

<script>
import FormButtons from '../../common/FormButtons';

export default {
    components: {
        'app-formbuttons': FormButtons,
    },

    props: {
        parentObject: Object
    },

    data() {
        return {
            formData: {
                id:         this.parentObject.data.id,
                positionid: this.parentObject.data.positionid,
                poscode:    this.parentObject.data.poscode,
                posdesc:    this.parentObject.data.posdesc,
                isdisable:  this.parentObject.data.isdisable,
                remark:     this.parentObject.data.remark,
                startdate:  this.parentObject.data.startdate,
                enddate:    this.parentObject.data.enddate,
                positioncount:      this.parentObject.data.positioncount,
                repposid:   this.parentObject.data.repposid,
                compnyid:   this.parentObject.data.compnyid,
                brchid:     this.parentObject.data.brchid,
                divnid:     this.parentObject.data.divnid,
                deptid:     this.parentObject.data.deptid,
                sectioid:   this.parentObject.data.sectioid,
                unitid:     this.parentObject.data.unitid,
                jobid:      this.parentObject.data.jobid,
                grdid:      this.parentObject.data.grdid,
                emptypeid:  this.parentObject.data.emptypeid,
                stamp:      this.parentObject.data.stamp,
                userid:     this.parentObject.data.userid,
            },

            // Position dropdown (parent selection)
            positionList: [],
            positions:    [],

            // Add-new-position dialog
            newPosDialog: false,
            newPos: { poscode: '', posdesc: '', isdisable: false, remark: '' },
            newPosErr: false,
            newPosErrMsg: '',

            reportingPositionList: [],
            reportingPositions:    [],
            reportingPositionCode: '',

            companyList:    [], companies:   [], companyCode:    '',
            branchList:     [], branches:    [], branchCode:     '',
            divisionList:   [], divisions:   [], divisionCode:   '',
            departmentList: [], departments: [], departmentCode: '',
            sectionList:    [], sections:    [], sectionCode:    '',
            unitList:       [], units:       [], unitCode:       '',
            jobList:        [], jobs:        [], jobCode:        '',
            gradeList:      [], grades:      [], gradeCode:      '',
            emptypeList:    [], emptypes:    [], emptypeCode:    '',

            rules: {
                required: value => !!value || this.$t('reportsparam.required'),
            },

            loading: false,
            search: this.parentObject.searchnew,
        };
    },

    methods: {
        // ── Drop-down loaders ──────────────────────────────────────────────

        loadPositionList() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/position/all/op' })
                .then(res => {
                    vm.positions = res.data.rec;
                    vm.positionList = res.data.rec.map(p => ({
                        text: p.poscode + ' ' + p.posdesc,
                        value: p.id,
                    }));
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        changePositionHandler(id) {
            if (!id) return;
            const pos = this.positions.find(p => p.id === id);
            if (!pos) return;
            this.formData.poscode   = pos.poscode;
            this.formData.posdesc   = pos.posdesc;
            this.formData.isdisable = pos.isdisable === 'N' ? false
                                    : pos.isdisable === 'Y' ? true
                                    : Boolean(pos.isdisable);
            this.formData.remark    = pos.remark || '';
            this.updateValue();
        },

        saveNewPosition() {
            const vm = this;
            if (!this.newPos.poscode || !this.newPos.posdesc) {
                this.newPosErr    = true;
                this.newPosErrMsg = this.$t('reportsparam.required');
                return;
            }
            const payload = {
                poscode:   this.newPos.poscode,
                posdesc:   this.newPos.posdesc,
                isdisable: this.newPos.isdisable ? 1 : 0,
                remark:    this.newPos.remark,
                userid:    this.formData.userid,
                stamp:     0,
            };
            this.$store.dispatch('post', { url: '/position/create', payload })
                .then(res => {
                    if (res.data.error) {
                        vm.newPosErr    = true;
                        vm.newPosErrMsg = res.data.error;
                        return;
                    }
                    const newId = res.data.rec && res.data.rec.id ? res.data.rec.id : null;
                    // Refresh position list then auto-select the newly created one
                    vm.loadPositionList().then(() => {
                        if (newId) {
                            vm.formData.positionid = newId;
                            vm.changePositionHandler(newId);
                        }
                    });
                    vm.closeNewPosDialog();
                })
                .catch(err => {
                    vm.newPosErr    = true;
                    vm.newPosErrMsg = err;
                });
        },

        closeNewPosDialog() {
            this.newPosDialog = false;
            this.newPosErr    = false;
            this.newPosErrMsg = '';
            this.newPos = { poscode: '', posdesc: '', isdisable: false, remark: '' };
        },

        loadReportingPosition() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/position/all/op' })
                .then(res => {
                    vm.reportingPositions = res.data.rec;
                    vm.reportingPositionList = res.data.rec.map(p => ({
                        text: p.poscode + ' ' + p.posdesc,
                        value: p.id,
                    }));
                    if (vm.formData.repposid) vm.changeReportingPositionHandler(vm.formData.repposid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadCompanies() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/company/all' })
                .then(res => {
                    vm.companies = res.data.rec;
                    vm.companyList = res.data.rec.map(c => ({ text: c.compnyname, value: c.id }));
                    if (vm.formData.compnyid) vm.changeCompanyHandler(vm.formData.compnyid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadBranches() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/branch/all' })
                .then(res => {
                    vm.branches = res.data.rec;
                    vm.branchList = res.data.rec.map(b => ({ text: b.brchname, value: b.id }));
                    if (vm.formData.brchid) vm.changeBranchHandler(vm.formData.brchid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadDivisions() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/division/all' })
                .then(res => {
                    vm.divisions = res.data.rec;
                    vm.divisionList = res.data.rec.map(d => ({ text: d.divnname, value: d.id }));
                    if (vm.formData.divnid) vm.changeDivisionHandler(vm.formData.divnid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadDepartments() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/department/all' })
                .then(res => {
                    vm.departments = res.data.rec;
                    vm.departmentList = res.data.rec.map(d => ({ text: d.deptname, value: d.id }));
                    if (vm.formData.deptid) vm.changeDepartmentHandler(vm.formData.deptid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadSections() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/section/all' })
                .then(res => {
                    vm.sections = res.data.rec;
                    vm.sectionList = res.data.rec.map(s => ({ text: s.sectioname, value: s.id }));
                    if (vm.formData.sectioid) vm.changeSectionHandler(vm.formData.sectioid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadUnits() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/unit/all' })
                .then(res => {
                    vm.units = res.data.rec;
                    vm.unitList = res.data.rec.map(u => ({ text: u.unitname, value: u.id }));
                    if (vm.formData.unitid) vm.changeUnitHandler(vm.formData.unitid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadJobs() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/job/all' })
                .then(res => {
                    vm.jobs = res.data.rec;
                    vm.jobList = res.data.rec
                        .filter(j => j.isdisable === 'N')
                        .map(j => ({ text: j.jobtitle, value: j.id }));
                    if (vm.formData.jobid) vm.changeJobHandler(vm.formData.jobid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadGrades() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/grade/all' })
                .then(res => {
                    vm.grades = res.data.rec;
                    vm.gradeList = res.data.rec.map(g => ({ text: g.grddesc, value: g.id }));
                    if (vm.formData.grdid) vm.changeGradeHandler(vm.formData.grdid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        loadEmpTypes() {
            const vm = this;
            return this.$store.dispatch('get', { url: '/emptype/all' })
                .then(res => {
                    vm.emptypes = res.data.rec;
                    vm.emptypeList = res.data.rec.map(e => ({ text: e.emptype, value: e.id }));
                    if (vm.formData.emptypeid) vm.changeEmpTypeHandler(vm.formData.emptypeid);
                })
                .catch(err => vm.$emit('error', { isErr: true, errMsg: err }));
        },

        async loadDropDowns() {
            this.loading = true;
            try {
                await this.loadPositionList();
                await this.loadReportingPosition();
                await this.loadCompanies();
                await this.loadBranches();
                await this.loadDivisions();
                await this.loadDepartments();
                await this.loadSections();
                await this.loadUnits();
                await this.loadGrades();
                await this.loadEmpTypes();
                await this.loadJobs();
            } finally {
                this.loading = false;
            }
        },

        // ── Change handlers ────────────────────────────────────────────────

        changeReportingPositionHandler(id) {
            if (!id) { this.reportingPositionCode = ''; return; }
            const item = this.reportingPositions.find(p => p.id === id);
            if (item) this.reportingPositionCode = item.poscode;
        },

        changeCompanyHandler(id) {
            if (!id) { this.companyCode = ''; return; }
            const item = this.companies.find(c => c.id === id);
            if (item) this.companyCode = item.compnycode;
        },

        changeBranchHandler(id) {
            if (!id) { this.branchCode = ''; return; }
            const item = this.branches.find(b => b.id === id);
            if (item) this.branchCode = item.brchcode;
        },

        changeDivisionHandler(id) {
            if (!id) { this.divisionCode = ''; return; }
            const item = this.divisions.find(d => d.id === id);
            if (item) this.divisionCode = item.divncode;
        },

        changeDepartmentHandler(id) {
            if (!id) { this.departmentCode = ''; return; }
            const item = this.departments.find(d => d.id === id);
            if (item) this.departmentCode = item.deptcode;
        },

        changeSectionHandler(id) {
            if (!id) { this.sectionCode = ''; return; }
            const item = this.sections.find(s => s.id === id);
            if (item) this.sectionCode = item.sectiocode;
        },

        changeUnitHandler(id) {
            if (!id) { this.unitCode = ''; return; }
            const item = this.units.find(u => u.id === id);
            if (item) this.unitCode = item.unitcode;
        },

        changeJobHandler(id) {
            if (!id) { this.jobCode = ''; return; }
            const item = this.jobs.find(j => j.id === id);
            if (item) {
                this.jobCode = item.jobcode;
                if (item.gradeid)   { this.formData.grdid    = item.gradeid;   this.changeGradeHandler(item.gradeid); }
                if (item.emptypeid) { this.formData.emptypeid = item.emptypeid; this.changeEmpTypeHandler(item.emptypeid); }
            }
        },

        changeGradeHandler(id) {
            if (!id) { this.gradeCode = ''; return; }
            const item = this.grades.find(g => g.id === id);
            if (item) this.gradeCode = item.grdcode;
        },

        changeEmpTypeHandler(id) {
            if (!id) { this.emptypeCode = ''; return; }
            const item = this.emptypes.find(e => e.id === id);
            if (item) this.emptypeCode = item.emptype;
        },

        // ── Form actions ───────────────────────────────────────────────────

        updateValue() {
            this.$emit('input', {
                id:         this.formData.id,
                positionid: this.formData.positionid,
                poscode:    this.formData.poscode,
                posdesc:    this.formData.posdesc,
                isdisable: this.formData.isdisable,
                remark:    this.formData.remark,
                startdate: this.formData.startdate,
                enddate:   this.formData.enddate,
                positioncount:     this.formData.positioncount,
                repposid:  this.formData.repposid,
                compnyid:  this.formData.compnyid,
                brchid:    this.formData.brchid,
                divnid:    this.formData.divnid,
                deptid:    this.formData.deptid,
                sectioid:  this.formData.sectioid,
                unitid:    this.formData.unitid,
                jobid:     this.formData.jobid,
                grdid:     this.formData.grdid,
                emptypeid: this.formData.emptypeid,
                stamp:     this.formData.stamp,
                userid:    this.formData.userid,
            });
        },

        newHandler()    { this.$emit('new'); },
        deleteHandler() { this.$emit('delete'); },

        saveHandler() {
            if (!this.$refs.form.validate()) return;
            this.updateValue();
            this.$emit('save');
        },

        cancelHandler() {
            this.$router.push({ name: 'positionlist', params: { search: this.search } });
        },
    },

    async created() {
        await this.loadDropDowns();
    },
};
</script>

<style scoped>
.section-heading {
    font-size: 1rem;
    font-weight: 700;
    color: #1E293B;
    margin-bottom: 12px;
}
</style>
